import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { InputText } from 'primereact/inputtext'
import { t } from 'i18next'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { supabase } from 'Services/supabaseClient'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import { DialogModal } from 'Components/UIComponents'
import { commonMethods } from 'Utils/commonMethods'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'

const UserManagement = ({ setLoading }) => {
  const history = useHistory()

  const categories = [
    { name: 'Regular', key: 'R' },
    { name: 'Gold', key: 'G' },
    { name: 'Platinum', key: 'P' }
  ]

  const subscriptionClass = (type) => {
    if (type === 'Gold') return 'badge-gold'
    if (type === 'Platinum') return 'badge-platinum'

    return 'badge-regular'
  }

  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [allUsersManagementData, setAllUserManagementData] = useState([])
  const [usersData, setUsersData] = useState([])
  const [allUsersData, setAllUsersData] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('')
  const filterOptions = [{ name: t('All'), value: '' }, { name: t('Bot User'), value: 'BOT' }, { name: t('Internal'), value: 'INTERNAL' }, ...categories.map((c) => ({ name: c.name, value: c.name }))]
  const [dialogState, setDialogState] = useState({
    showModal: false,
    modalType: '',
    message: '',
    onSuccess: () => {},
    onHide: () => {},
    onRetry: () => handleDismiss(),
    onDismiss: () => handleDismiss()
  })

  const rowCount = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobileRowCount : 5
  const paginationButtons = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobilePaginationButtonCount : TABLE_CONST.paginationButtonCount

  useEffect(() => {
    fetchUsersManagementData()

    const handleWindowResize = () => {
      setWindowSize(window.innerWidth)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const fetchUsersManagementData = async () => {
    try {
      setLoading(true)

      const { data: usersData, error: usersError } = await supabase.from('users').select('*')

      if (usersError) {
        setDialogState({
          showModal: true,
          modalType: 'error',
          message: t('SORRY_COULD_NOT_FETCH_THE_DATA_PLEASE_TRY_AGAIN_LATER'),
          onHide: handleDismiss,
          onRetry: () => fetchUsersManagementData()
        })

        return
      }

      setAllUserManagementData(usersData)

      const users = usersData.map((user) => ({
        userId: user.userid,
        firstName: user.first_name,
        email: user.email,
        botUser: user.can_act_as_bot,
        isInternalUser: user.is_internal_user,
        subscription: categories.find((item) => item.key === user?.subscription_type)?.name,
        date: commonMethods.utcToLocalTime(user.created_at)
      }))

      setUsersData(users)
      setAllUsersData(users)
    } catch (error) {
      setDialogState({
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_FETCH_THE_DATA_PLEASE_TRY_AGAIN_LATER'),
        onConfirmation: () => fetchUsersManagementData(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (e) => {
    const searchValue = e.target?.value?.toLowerCase()

    setSearchTerm(searchValue)

    if (!searchValue) {
      setUsersData(allUsersData)
    } else {
      const filteredUsers = allUsersData?.filter((user) => user?.firstName?.toLowerCase()?.includes(searchValue) || user?.email?.toLowerCase()?.includes(searchValue) || user?.userId?.toString()?.includes(searchValue))

      setUsersData(filteredUsers)
    }

    applyFilters(selectedFilter, searchValue)
  }

  const handleDismiss = () => {
    setDialogState((prevState) => ({ ...prevState, showModal: false }))
  }

  const handleUserSettings = (rowData) => {
    const updatedData = allUsersManagementData?.filter((item) => item?.userid === rowData.userId)

    history.push({
      pathname: ROUTE_STRINGS.userManagementSetting,
      state: updatedData
    })
  }

  const applyFilters = (filterValue = selectedFilter, searchValue = searchTerm) => {
    let filtered = allUsersData ?? []

    if (filterValue) {
      if (filterValue === 'INTERNAL') {
        filtered = filtered.filter((u) => u.isInternalUser)
      } else if (filterValue === 'BOT') {
        filtered = filtered.filter((u) => u.botUser)
      } else {
        filtered = filtered.filter((u) => u.subscription === filterValue)
      }
    }

    if (searchValue) {
      const sv = searchValue.toLowerCase()

      filtered = filtered.filter((user) => user?.firstName?.toLowerCase()?.includes(sv) || user?.email?.toLowerCase()?.includes(sv) || user?.userId?.toString()?.includes(sv))
    }

    setUsersData(filtered)
  }

  const handleFilterChange = (e) => {
    const val = e.value

    setSelectedFilter(val)
    applyFilters(val, searchTerm)
  }

  const subscriptionBody = (rowData) => (
    <div className="subscription-badges">
      {rowData?.subscription && <span className={`badge ${subscriptionClass(rowData.subscription)}`}>{rowData.subscription}</span>}
      {rowData?.isInternalUser && <span className="badge badge-internal">{t('Internal')}</span>}
      {rowData?.botUser && <span className="badge badge-bot">{t('Bot User')}</span>}
    </div>
  )

  const actionTemplate = (rowData) => (
    <div className="action-container">
      <Button type="button" icon="pi pi-cog" className="users-setting-icon" tooltip={t('Settings')} tooltipOptions={{ position: 'top' }} onClick={() => handleUserSettings(rowData)} />
    </div>
  )

  const columns = [
    { dataField: 'userId', text: t('Id'), className: 'id-column', isSortable: true },
    { dataField: 'firstName', text: t('NAME'), className: 'name-column', isSortable: true },
    { dataField: 'email', text: t('EMAIL'), className: 'email-column', isSortable: true },
    { dataField: 'date', text: t('DATE'), className: 'phone-column', isSortable: true },
    { dataField: 'subscription', text: t('User Info'), className: 'subscription-column', isSortable: false, body: subscriptionBody },
    { body: actionTemplate, text: t('ACTIONS'), className: 'edit-column' }
  ]

  return (
    <LayoutContainer title={t('USER_MANAGEMENT')} breadCrumbsTitle={'userManagement'}>
      <div className="user-management-div">
        <div className="search-filter-div">
          <div>
            <Dropdown optionLabel="name" options={filterOptions} value={selectedFilter} onChange={handleFilterChange} placeholder={t('Select User Info')} className="user-management-dropdown" />{' '}
          </div>
          <div className="search-div p-input-icon-left">
            <i className="pi pi-search" data-testid="search-icon" />
            <InputText value={searchTerm} placeholder={t('SEARCH')} onChange={handleSearchChange} />
          </div>
        </div>
        <div className="table-div">
          <DataTable value={usersData} removableSort paginator rows={rowCount} pageLinkSize={paginationButtons} scrollHeight="550px" scrollable>
            {columns.map((col, i) => (
              <Column key={i} field={col.dataField} sortable={col.isSortable} header={col.text} body={col.body} className={col.className} />
            ))}
          </DataTable>
        </div>
      </div>
      <DialogModal {...dialogState} />
    </LayoutContainer>
  )
}

export default Loader(UserManagement)
